import { Component, ChangeDetectionStrategy, DestroyRef, Injector, OnInit, inject, runInInjectionContext, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import type { Observable } from 'rxjs';
import { SelectorAreaofinterestsComponent } from '../../../core/selector-areaofinterests/selector-areaofinterests.component';

type LoadFn<T> = (ctx: { injector: Injector }) => Observable<T>;

@Component({
  selector: 'app-formly-core-selector-areaofinterests',
  standalone: true,
  imports: [ReactiveFormsModule, TranslateModule, FormlyModule, SelectorAreaofinterestsComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div [attr.data-field-id]="field.id" class="flex flex-col gap-2">
      @if (props.label) {
        <span [class]="labelClass()">
          {{ props.label | translate }}
        </span>
      }
      <div [class]="wrapClass()">
        <app-selector-areaofinterests
          [maxAmount]="$any(props['maxSelections'] || 15)"
          [data]="$any(optionsValue())"
          [formControl]="$any(formControl)"></app-selector-areaofinterests>
      </div>
      @if (props['maxSelections']) {
        <span [class]="counterClass()">
          {{ selectedCount() }} / {{ props['maxSelections'] }}
        </span>
      }
      @if (isInvalid()) {
        <span class="font-base text-[12px] text-mty-danger">
          {{ 'This field is required' | translate }}
        </span>
      }
    </div>
  `,
})
export class CoreSelectorAreaofinterestsFieldComponent extends FieldType<FieldTypeConfig> implements OnInit {
  private injector = inject(Injector);
  private destroyRef = inject(DestroyRef);

  private loadedOptions = signal<unknown[] | null>(null);

  ngOnInit(): void {
    const loadFn = this.props['loadOptions'] as LoadFn<unknown[]> | undefined;
    if (typeof loadFn !== 'function') return;
    runInInjectionContext(this.injector, () => {
      loadFn({ injector: this.injector })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((result) => this.loadedOptions.set(result ?? []));
    });
  }

  optionsValue(): unknown[] {
    const loaded = this.loadedOptions();
    if (loaded !== null) return loaded;
    return (this.props['options'] as unknown[]) ?? [];
  }

  selectedCount(): number {
    const v = this.formControl?.value;
    return Array.isArray(v) ? v.length : 0;
  }

  counterClass(): string {
    const max = this.props['maxSelections'] as number | undefined;
    const atCap = !!max && this.selectedCount() >= max;
    return atCap
      ? 'font-base text-xs text-mty-danger font-semibold'
      : 'font-base text-xs text-black/55';
  }

  isInvalid(): boolean {
    const c = this.formControl;
    if (!c) return false;
    return c.invalid && (c.dirty || c.touched);
  }

  labelClass(): string {
    const base = 'inline-block font-alternate text-xs font-semibold tracking-wider uppercase';
    return this.isInvalid()
      ? `${base} text-mty-danger`
      : `${base} text-[var(--mat-sys-on-surface-variant)]`;
  }

  wrapClass(): string {
    const base = 'rounded-2xl p-2 transition-colors';
    return this.isInvalid() ? `${base} bg-mty-danger/5 ring-1 ring-mty-danger/30` : base;
  }
}
